import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import * as authApi from '@/api/auth';
import type { CurrentUser, UserRole } from '@/types/contracts';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null);
  const isInitialized = ref(false);
  const isLoading = ref(false);

  const isAuthenticated = computed(() => user.value !== null);

  async function initialize(): Promise<void> {
    if (isInitialized.value) return;
    try {
      user.value = await authApi.getCurrentUser();
    } catch {
      user.value = null;
    } finally {
      isInitialized.value = true;
    }
  }

  async function login(credentials: authApi.LoginRequest): Promise<void> {
    isLoading.value = true;
    try {
      user.value = await authApi.login(credentials);
      isInitialized.value = true;
    } finally {
      isLoading.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout();
    } finally {
      user.value = null;
      isInitialized.value = true;
    }
  }

  function hasRole(roles?: UserRole[]): boolean {
    return !roles?.length || Boolean(user.value && roles.includes(user.value.role));
  }

  return { user, isInitialized, isLoading, isAuthenticated, initialize, login, logout, hasRole };
});
